import type { ApiAddress } from 'api/types/ApiAddress';
import { isEmpty } from 'utils/functional/poly';
import { setGMapsError } from 'zustand-stores/gMapsErrorStore';

function getTextSearchQuery(address: Partial<ApiAddress>) {
  const { line1, city, state, zipcode, country } = address;
  return [line1, city, state, zipcode, country]
    .filter((value) => value)
    .join(',');
}

function compareNoCase(s1?: string, s2?: string) {
  return s1 && s2 ? s1.toLowerCase() === s2.toLowerCase() : s1 === s2;
}

type Args = {
  currentAddress: Partial<ApiAddress>;
  setFetchingAddress: (arg0: boolean) => void;
};

export async function lookupAddress({
  currentAddress,
  setFetchingAddress,
}: Args) {
  const addressIsEmpty = currentAddress.locationHidden
    ? isEmpty(currentAddress.city) && isEmpty(currentAddress.state)
    : isEmpty(currentAddress.line1) &&
      isEmpty(currentAddress.city) &&
      isEmpty(currentAddress.state) &&
      isEmpty(currentAddress.zipcode);

  if (addressIsEmpty) {
    setFetchingAddress(false);
    return Promise.resolve({});
  }

  try {
    const { Geocoder } = (await google.maps.importLibrary(
      'geocoding',
    )) as google.maps.GeocodingLibrary;

    return await new Promise((resolve) => {
      new Geocoder().geocode(
        {
          address: getTextSearchQuery(currentAddress),
          componentRestrictions: {
            country: currentAddress.country,
          },
        },
        // oxlint-disable-next-line typescript/no-explicit-any
        (results: any) => {
          // prevent results that could have come back later from overriding this.
          const update = {};

          if (results && results.length > 0) {
            const [result] = results;

            if (
              currentAddress.country &&
              ['US', 'CA'].includes(currentAddress.country)
            ) {
              const stateComponent = result.address_components.find(
                // oxlint-disable-next-line typescript/no-explicit-any
                (c: any) => c.types.includes('administrative_area_level_1'),
              );

              if (
                stateComponent &&
                (compareNoCase(
                  stateComponent.long_name,
                  currentAddress.state,
                ) ||
                  compareNoCase(
                    stateComponent.short_name,
                    currentAddress.state,
                  ))
              ) {
                // Only update the address if there is a valid state / province
                // @ts-expect-error TS(2339): Property 'stateCode' does not exist on type '{}'.
                update.stateCode = stateComponent.short_name;
                // @ts-expect-error TS(2339): Property 'latitude' does not exist on type '{}'.
                update.latitude = result.geometry.location.lat();
                // @ts-expect-error TS(2339): Property 'longitude' does not exist on type '{}'.
                update.longitude = result.geometry.location.lng();
                // @ts-expect-error TS(2339): Property 'latLongAddress' does not exist on type '... Remove this comment to see the full error message
                update.latLongAddress = result.formatted_address;
              }
            } else if (!result.types.includes('country')) {
              // @ts-expect-error TS(2339): Property 'latitude' does not exist on type '{}'.
              update.latitude = result.geometry.location.lat();
              // @ts-expect-error TS(2339): Property 'longitude' does not exist on type '{}'.
              update.longitude = result.geometry.location.lng();
              // @ts-expect-error TS(2339): Property 'latLongAddress' does not exist on type '... Remove this comment to see the full error message
              update.latLongAddress = result.formatted_address;
            }
          }

          setFetchingAddress(false);
          resolve(update);
        },
      );
    });
  } catch (error) {
    if (error instanceof Error) {
      setGMapsError(error.message);
    }
  }
}
